import {
  Box,
  iconButtonClasses,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { IconEdit, IconTrash } from 'assets/img';
import { LwIconButton } from 'redesign';
import { PartnerWorkerCla } from 'partner/shared/services/partner-worker-service.types';
import { useAlert } from 'shared/components/alerts';
import { mapClasToForm } from './cla-list-table.mappers';
import { useDeletePartnerWorkerCla } from '../hooks/use-delete-partner-worker-cla';

export type ClaListTableProps = {
  workerId: string;
  clas: PartnerWorkerCla[];
};

const formatDate = (iso: string): string => DateTime.fromISO(iso).toFormat('dd/MM/yyyy');

export const ClaListTable = ({ workerId, clas }: ClaListTableProps) => {
  const { t } = useTranslation('partner-screen-specific');
  const { alertSuccess, alertError } = useAlert();
  const { mutate: deleteCla, isLoading: isDeleting, variables } = useDeletePartnerWorkerCla();
  const rows = mapClasToForm(clas);

  const handleDelete = (collectiveLabourAgreementId: string) => {
    deleteCla(
      { partnerWorkerId: workerId, collectiveLabourAgreementId },
      {
        onSuccess: () =>
          alertSuccess(
            t('partner-worker-detail.cla-list.deleted.success', {
              ns: 'partner-screen-specific',
            })
          ),
        onError: (error) => alertError(error),
      }
    );
  };

  return (
    <StyledTable>
      <StyledTableHead>
        <TableRow>
          <HeaderCell>
            <Typography variant="body2" component="span">
              {t('partner-worker-detail.cla-list.headers.name', {
                ns: 'partner-screen-specific',
              })}
            </Typography>
          </HeaderCell>
          <HeaderCell>
            <Typography variant="body2" component="span">
              {t('partner-worker-detail.cla-list.headers.scales', {
                ns: 'partner-screen-specific',
              })}
            </Typography>
          </HeaderCell>
          <HeaderCell>
            <Typography variant="body2" component="span">
              {t('partner-worker-detail.cla-list.headers.step', {
                ns: 'partner-screen-specific',
              })}
            </Typography>
          </HeaderCell>
          <HeaderCell>
            <Typography variant="body2" component="span">
              {t('partner-worker-detail.cla-list.headers.start-date', {
                ns: 'partner-screen-specific',
              })}
            </Typography>
          </HeaderCell>
          <HeaderCell>
            <Typography variant="body2" component="span">
              {t('partner-worker-detail.cla-list.headers.end-date', {
                ns: 'partner-screen-specific',
              })}
            </Typography>
          </HeaderCell>
          <ActionsHeaderCell>
            <Typography variant="body2" component="span">
              {t('partner-worker-detail.cla-list.headers.actions', {
                ns: 'partner-screen-specific',
              })}
            </Typography>
          </ActionsHeaderCell>
        </TableRow>
      </StyledTableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.key}>
            {row.claNameRowSpan !== undefined ? (
              <ClaNameCell rowSpan={row.claNameRowSpan}>
                <Typography variant="body1" component="span">
                  {row.claName ?? ''}
                </Typography>
              </ClaNameCell>
            ) : null}
            {row.scaleRowSpan !== undefined ? (
              <ScaleCell rowSpan={row.scaleRowSpan}>
                {row.scale ? (
                  <ScaleChip>
                    <ScaleChipText variant="body2" as="span">
                      {row.scale.scale}
                    </ScaleChipText>
                  </ScaleChip>
                ) : null}
              </ScaleCell>
            ) : null}
            <BodyCell>
              <Typography variant="body1" component="span">
                {row.step ?? ''}
              </Typography>
            </BodyCell>
            <BodyCell>
              <Typography variant="body1" component="span">
                {row.startDate ? formatDate(row.startDate) : ''}
              </Typography>
            </BodyCell>
            <BodyCell>
              {row.endDate ? (
                <Typography variant="body1" component="span">
                  {formatDate(row.endDate)}
                </Typography>
              ) : row.step ? (
                <ActiveText variant="body1" as="span">
                  {t('partner-worker-detail.cla-list.step.active', {
                    ns: 'partner-screen-specific',
                  })}
                </ActiveText>
              ) : null}
            </BodyCell>
            {row.claNameRowSpan !== undefined ? (
              <ActionsCell rowSpan={row.claNameRowSpan}>
                <ActionsWrapper>
                  <BorderlessIconButton
                    onClick={() => {}}
                    color="secondary"
                    iconColorMode="fill"
                    size="small"
                    aria-label={t('partner-worker-detail.cla-list.actions.edit.label', {
                      ns: 'partner-screen-specific',
                    })}
                    disabled
                  >
                    <IconEdit width={18} height={18} />
                  </BorderlessIconButton>
                  <BorderlessIconButton
                    onClick={() => handleDelete(row.claId)}
                    color="error"
                    iconColorMode="stroke"
                    size="small"
                    aria-label={t('partner-worker-detail.cla-list.actions.delete.label', {
                      ns: 'partner-screen-specific',
                    })}
                    disabled={isDeleting && variables?.collectiveLabourAgreementId === row.claId}
                  >
                    <IconTrash width={18} height={18} />
                  </BorderlessIconButton>
                </ActionsWrapper>
              </ActionsCell>
            ) : null}
          </TableRow>
        ))}
      </TableBody>
    </StyledTable>
  );
};

const StyledTable = styled(Table)({
  width: '100%',
  borderCollapse: 'collapse',
});

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.lwPrimaryAccentDark[10]}`,
}));

const HeaderCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.lwBlack[40],
  padding: theme.spacing(2, 6),
  verticalAlign: 'middle',
  letterSpacing: '0.3px',
  textTransform: 'uppercase',
}));

const ActionsHeaderCell = styled(HeaderCell)({
  textAlign: 'right',
});

const BodyCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.lwBlack[60],
  borderBottom: `1px solid ${theme.palette.lwPrimaryAccentDark[5]}`,
  padding: theme.spacing(2, 6),
  verticalAlign: 'middle',
}));

const ClaNameCell = styled(BodyCell)(({ theme }) => ({
  color: theme.palette.lwBlack[100],
  borderRight: `1px solid ${theme.palette.lwPrimaryAccentDark[5]}`,
}));

const ScaleCell = styled(BodyCell)(({ theme }) => ({
  borderRight: `1px solid ${theme.palette.lwPrimaryAccentDark[5]}`,
}));

const ActionsCell = styled(BodyCell)(({ theme }) => ({
  borderLeft: `1px solid ${theme.palette.lwPrimaryAccentDark[5]}`,
}));

const ActionsWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
}));

const BorderlessIconButton = styled(LwIconButton)(({ theme }) => ({
  [`&.${iconButtonClasses.colorSecondary}`]: {
    border: 'none',
    backgroundColor: 'transparent',
    '&:hover': {
      border: 'none',
      backgroundColor: theme.palette.lwPrimaryAccentLight[5],
      boxShadow: 'none',
    },
    '&:active': {
      border: 'none',
    },
  },
  [`&.${iconButtonClasses.colorError}`]: {
    '&:hover': {
      backgroundColor: theme.palette.lwPrimaryAccentLight[5],
      boxShadow: 'none',
    },
  },
}));

const ScaleChip = styled(Box)(({ theme }) => ({
  display: 'inline-block',
  backgroundColor: theme.palette.lwPrimaryAccentLight[10],
  borderRadius: 999,
  padding: '2px 8px',
  whiteSpace: 'nowrap',
}));

const ScaleChipText = styled(Typography)(({ theme }) => ({
  color: theme.palette.lwPrimaryAccentLight[100],
}));

const ActiveText = styled(Typography)(({ theme }) => ({
  color: theme.palette.lwBlack[30],
}));
